import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'sonner';
import { evaluationService } from '../../services/EvaluationService';
import { ROUTE_PATHS } from '../../constants/routePaths';

interface Evaluation {
  evaluation_id: number;
  name: string;
  dataset_name: string;
  judge_model: string;
  status: string;
  overall_score?: number;
  created_at?: string;
}

export function EvaluationPage() {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEvaluations();
  }, []);

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const data = await evaluationService.getAllEvaluations();
      setEvaluations(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(ROUTE_PATHS.EVALUATION_ADD);
  };

  const handleView = (evaluation: Evaluation) => {
    navigate(`${ROUTE_PATHS.EVALUATION}/${evaluation.evaluation_id}`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'success':
        return { bgcolor: '#dcfce7', color: '#166534' };
      case 'failure':
        return { bgcolor: '#fee2e2', color: '#991b1b' };
      case 'pending':
        return { bgcolor: '#fef3c7', color: '#92400e' };
      default:
        return { bgcolor: '#f1f5f9', color: '#475569' };
    }
  };

  return (
    <Box sx={{ p: 3 }}>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Evaluations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and view your model evaluations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            bgcolor: '#2563eb',
            '&:hover': { bgcolor: '#1d4ed8' },
          }}
        >
          Create Evaluation
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Dataset</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {evaluations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No evaluations found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              evaluations.map((evaluation) => (
                <TableRow key={evaluation.evaluation_id} hover>
                  <TableCell>{evaluation.name}</TableCell>
                  <TableCell>{evaluation.dataset_name}</TableCell>
                  <TableCell>{evaluation.judge_model}</TableCell>
                  <TableCell>
                    <Chip
                      label={evaluation.status}
                      size="small"
                      sx={getStatusColor(evaluation.status)}
                    />
                  </TableCell>
                  <TableCell>{formatDate(evaluation.created_at)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleView(evaluation)}
                      sx={{ color: '#2563eb' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
